// =============================================================================
// Helpers compartidos para las acciones de gestión de reuniones
// (cancelar / reprogramar) usadas por lista, calendario y kanban.
// =============================================================================

import { MeetingRequest, MeetingResponse, MeetingStatusResponse } from '../models/meeting.model';

/** Normaliza a minúsculas y sin acentos para comparar nombres de estado. */
function norm(s?: string): string {
  return (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(new RegExp('[\\u0300-\\u036f]', 'g'), '');
}

/** True si el estado representa una CANCELACIÓN (por nombre/label: "Cancelado", "Cancelada"...). */
export function isCancelStatus(status: { name?: string; label?: string } | null | undefined): boolean {
  if (!status) return false;
  const t = norm(status.name) + ' ' + norm(status.label);
  return t.includes('cancel');
}

/** True si el estado representa una REPROGRAMACIÓN (por nombre/label: "Reprogramado", "Reagendado"...). */
export function isRescheduleStatus(status: { name?: string; label?: string } | null | undefined): boolean {
  if (!status) return false;
  const t = norm(status.name) + ' ' + norm(status.label);
  return t.includes('reprogram') || t.includes('reschedul') || t.includes('reagend');
}

/**
 * INC 80 — ¿la reunión está CANCELADA?
 *
 * Mismo criterio que el backend (ver `MeetingRepository`): motivo de cancelación
 * informado **o** estado cuyo nombre/etiqueta contiene "cancel".
 *
 * Hacía falta porque el estado de cancelación es opcional: los estados son por negocio
 * (Nuevo, Confirmado, Finalizado…) y si el tenant no tiene uno llamado "Cancelada", al
 * cancelar solo queda el motivo — la reunión seguía figurando como "Confirmado" en lista,
 * kanban y detalle aunque el backend ya la ignoraba en la agenda.
 *
 * @param statuses catálogo de estados para resolver el nombre; opcional.
 */
export function isMeetingCancelled(
  meeting: { cancellationReasonText?: string | null; idMeetingStatus?: number | null } | null | undefined,
  statuses?: MeetingStatusResponse[] | null
): boolean {
  if (!meeting) return false;
  if (meeting.cancellationReasonText && meeting.cancellationReasonText.trim()) return true;
  if (!statuses?.length || meeting.idMeetingStatus == null) return false;
  return isCancelStatus(statuses.find((s) => s.id === meeting.idMeetingStatus));
}

/** Id del estado de cancelación configurado en el catálogo, si existe. */
export function findCancelStatusId(statuses: MeetingStatusResponse[]): number | undefined {
  return statuses.find((s) => isCancelStatus(s))?.id;
}

/** Id del estado de reprogramación configurado en el catálogo, si existe. */
export function findRescheduleStatusId(statuses: MeetingStatusResponse[]): number | undefined {
  return statuses.find((s) => isRescheduleStatus(s))?.id;
}

/**
 * Copia TODOS los campos mutables de la reunión a un MeetingRequest.
 * El PUT reemplaza el registro completo; omitir campos B13/booking
 * (timezone, video, pago, idBookingLink...) los borraría (INC 4).
 * videoRoomToken es read-only: se omite adrede (lo reutiliza el backend).
 */
export function toMeetingRequest(m: MeetingResponse): MeetingRequest {
  return {
    title: m.title,
    datetimeStart: m.datetimeStart,
    datetimeEnd: m.datetimeEnd,
    businessId: m.businessId,
    description: m.description,
    location: m.location,
    meetingUrl: m.meetingUrl,
    locationMapUrl: m.locationMapUrl,
    idMeetingType: m.idMeetingType,
    idMeetingStatus: m.idMeetingStatus,
    dealId: m.dealId,
    companyId: m.companyId,
    contactId: m.contactId,
    organizerId: m.organizerId,
    notes: m.notes,
    outcome: m.outcome,
    cancellationReasonText: m.cancellationReasonText,
    idCancellationReason: m.idCancellationReason,
    source: m.source,
    isNoShow: m.isNoShow,
    noShowDetectedAt: m.noShowDetectedAt,
    paymentStatus: m.paymentStatus,
    paymentAmount: m.paymentAmount,
    paymentIntentId: m.paymentIntentId,
    videoProvider: m.videoProvider,
    videoJoinUrl: m.videoJoinUrl,
    inviteeTimezone: m.inviteeTimezone,
    gdprConsentGiven: m.gdprConsentGiven,
    gdprConsentDatetime: m.gdprConsentDatetime,
    userAssignedByRoutingId: m.userAssignedByRoutingId,
    routingRuleAppliedId: m.routingRuleAppliedId,
    idBookingLink: m.idBookingLink,
    timezone: m.timezone,
    preQualificationData: m.preQualificationData,
    externalEventId: m.externalEventId,
  };
}
